import React from 'react';
import { useTranslation } from 'react-i18next';

const HumanBodyDiagram = ({ volumeData }) => {
  const { t } = useTranslation();

  // Mapping from muscle group to SVG parts
  // volumeData is expected to be { '脚': { front: 100, back: 50 }, ... }
  
  const getColor = (part, side = 'both') => {
    const data = volumeData[part] || { front: 0, back: 0 };
    let volume = 0;
    if (side === 'front') volume = data.front;
    else if (side === 'back') volume = data.back;
    else volume = Math.max(data.front, data.back);

    if (volume === 0) return '#262626'; // Dark gray for no volume
    
    // Normalize opacity based on the overall maximum volume across all parts/sides
    const allVolumes = Object.values(volumeData).flatMap(v => [v.front, v.back]);
    const maxVolume = Math.max(...allVolumes, 100); 
    
    const ratio = volume / maxVolume;
    const intensity = Math.sqrt(ratio); // Use sqrt for better visibility
    const opacity = Math.min(0.2 + intensity * 0.8, 1);
    
    return `rgba(255, 69, 0, ${opacity})`;
  };

  const muscleStyles = (part, side) => ({
    fill: getColor(part, side),
    transition: 'fill 0.5s ease',
    stroke: '#404040',
    strokeWidth: '0.5',
  });

  return (
    <div className="flex flex-col items-center gap-8 w-full p-4 bg-[#1c1c1e] rounded-xl border border-gray-800 shadow-lg">
      <div className="flex justify-around w-full gap-4">
        {/* Front View */}
        <div className="flex flex-col items-center">
          <span className="text-xs text-gray-400 mb-2 font-bold uppercase tracking-wider">{t('front_view')}</span>
          <svg viewBox="0 0 100 200" className="w-40 h-80 drop-shadow-[0_0_10px_rgba(255,69,0,0.1)]">
            {/* Front Head */}
            <circle cx="50" cy="15" r="10" fill="#262626" stroke="#404040" strokeWidth="0.5" />
            
            {/* Front Neck */}
            <rect x="47" y="25" width="6" height="5" fill="#262626" />

            {/* Traps (Front) - Colored by both Back and Shoulders for better intuition */}
            <path d="M35 30 Q50 25 65 30 L60 35 L40 35 Z" {...muscleStyles('肩', 'front')} />

            {/* Shoulders (Front Deltoids) - Expanded to cover top of arm */}
            <path d="M30 35 Q20 40 25 55 L35 55 L35 35 Z" {...muscleStyles('肩', 'front')} />
            <path d="M70 35 Q80 40 75 55 L65 55 L65 35 Z" {...muscleStyles('肩', 'front')} />

            {/* Chest */}
            <path d="M35 35 L65 35 L65 55 Q50 60 35 55 Z" {...muscleStyles('胸', 'front')} />

            {/* Biceps */}
            <path d="M22 55 Q20 70 25 85 L32 85 L32 55 Z" {...muscleStyles('腕', 'front')} />
            <path d="M78 55 Q80 70 75 85 L68 85 L68 55 Z" {...muscleStyles('腕', 'front')} />

            {/* Forearms (Front) */}
            <path d="M25 80 Q25 100 30 115 L35 115 L35 80 Z" {...muscleStyles('腕', 'front')} />
            <path d="M75 80 Q75 100 70 115 L65 115 L65 80 Z" {...muscleStyles('腕', 'front')} />

            {/* Abs */}
            <path d="M35 55 Q50 60 65 55 L65 85 Q50 90 35 85 Z" {...muscleStyles('腹筋', 'front')} />
            
            {/* Obliques/Waist */}
            <path d="M35 85 L65 85 L65 100 L35 100 Z" fill="#262626" stroke="#404040" strokeWidth="0.5" />

            {/* Quads */}
            <path d="M35 105 Q30 130 35 155 L48 155 L48 105 Z" {...muscleStyles('脚', 'front')} />
            <path d="M65 105 Q70 130 65 155 L52 155 L52 105 Z" {...muscleStyles('脚', 'front')} />

            {/* Calves (Front) */}
            <path d="M35 155 Q35 175 40 190 L45 190 L48 155 Z" {...muscleStyles('脚', 'front')} />
            <path d="M65 155 Q65 175 60 190 L55 190 L52 155 Z" {...muscleStyles('脚', 'front')} />
          </svg>
        </div>

        {/* Back View */}
        <div className="flex flex-col items-center">
          <span className="text-xs text-gray-400 mb-2 font-bold uppercase tracking-wider">{t('back_view')}</span>
          <svg viewBox="0 0 100 200" className="w-40 h-80 drop-shadow-[0_0_10px_rgba(255,69,0,0.1)]">
            {/* Back Head */}
            <circle cx="50" cy="15" r="10" fill="#262626" stroke="#404040" strokeWidth="0.5" />

            {/* Back Neck */}
            <rect x="47" y="25" width="6" height="5" fill="#262626" />

            {/* Traps (Back) - Also react to Shoulders for better intuition */}
            <path d="M35 30 Q50 20 65 30 L55 50 L45 50 Z" {...muscleStyles('肩', 'back')} />

            {/* Shoulders (Back Deltoids) */}
            <path d="M30 35 Q20 40 25 55 L35 55 L35 35 Z" {...muscleStyles('肩', 'back')} />
            <path d="M70 35 Q80 40 75 55 L65 55 L65 35 Z" {...muscleStyles('肩', 'back')} />

            {/* Upper Back (Lats/Rhomboids) */}
            <path d="M35 35 L45 50 L50 45 L55 50 L65 35 L65 65 Q50 75 35 65 Z" {...muscleStyles('背中', 'back')} />
            
            {/* Lower Back */}
            <path d="M35 65 Q50 75 65 65 L65 85 Q50 95 35 85 Z" {...muscleStyles('背中', 'back')} />

            {/* Triceps */}
            <path d="M22 55 Q20 70 25 85 L32 85 L32 55 Z" {...muscleStyles('腕', 'back')} />
            <path d="M78 55 Q80 70 75 85 L68 85 L68 55 Z" {...muscleStyles('腕', 'back')} />

            {/* Forearms (Back) */}
            <path d="M25 80 Q25 100 30 115 L35 115 L35 80 Z" {...muscleStyles('腕', 'back')} />
            <path d="M75 80 Q75 100 70 115 L65 115 L65 80 Z" {...muscleStyles('腕', 'back')} />

            {/* Glutes */}
            <path d="M35 85 L65 85 L65 110 Q50 115 35 110 Z" {...muscleStyles('脚', 'back')} />

            {/* Hamstrings */}
            <path d="M35 110 Q32 135 35 155 L48 155 L48 110 Z" {...muscleStyles('脚', 'back')} />
            <path d="M65 110 Q68 135 65 155 L52 155 L52 110 Z" {...muscleStyles('脚', 'back')} />

            {/* Calves (Back) */}
            <path d="M35 155 Q35 178 40 190 L45 190 L48 155 Z" {...muscleStyles('脚', 'back')} />
            <path d="M65 155 Q65 178 60 190 L55 190 L52 155 Z" {...muscleStyles('脚', 'back')} />
          </svg>
        </div>
      </div>
      
      {/* Legend */}
      <div className="flex items-center gap-4 mt-2">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-[#262626] border border-gray-600 rounded"></div>
          <span className="text-[10px] text-gray-500 font-bold uppercase">{t('no_activity')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-8 h-2 bg-gradient-to-r from-[#262626] to-[rgba(255,69,0,1)] rounded"></div>
          <span className="text-[10px] text-gray-500 font-bold uppercase">{t('training_intensity')}</span>
        </div>
      </div>
    </div>
  );
};

export default HumanBodyDiagram;
